import React from 'react'
import { Icon, Spinner } from 'sidecar-components'

import { getAttachmentIconName, isDisplayableThumbnail } from './utils'

/**
 * A single attachment on a message, rendered as a preview when Jira gave us a
 * thumbnail and as a named file otherwise. Either way it downloads on click.
 *
 * @param {object} attachment - The attachment to render
 * @param {string} attachment.attachmentId - The Jira attachment id
 * @param {string} attachment.filename - The name of the file
 * @param {string} [attachment.contentType] - Mime type of the thumbnail, when
 *   there is one
 * @param {string} [attachment.content] - Base64 thumbnail data, when there is
 *   one
 * @param {boolean} busy - Whether this attachment is currently downloading
 * @param {boolean} error - Whether this attachment failed to download
 * @param {function} onDownload - Called with the attachment when clicked
 * @returns React component
 */
export const MessageAttachment = ({ attachment, busy, error, onDownload }) => {
  const { attachmentId, filename, contentType, content } = attachment
  // Jira has no filename for some attachments, and "Download null" tells a
  // member nothing
  const displayName = filename || 'Attachment'

  return (
    <div className="mb-1">
      <button
        type="button"
        aria-label={`Download ${displayName}`}
        data-testid={`btn_downloadAttachmentId_${attachmentId}`}
        onClick={e => {
          e.preventDefault()
          onDownload(attachment)
        }}
      >
        {isDisplayableThumbnail(attachment) ? (
          <img
            id={attachmentId}
            src={`data:${contentType}; base64, ${content}`}
            alt={displayName}
          />
        ) : (
          <span className="flex items-center gap-1 inherit-link-color underline text-left">
            <Icon
              name={getAttachmentIconName(filename)}
              size="sm"
              className="inherit shrink-0"
            />
            {displayName}
          </span>
        )}
      </button>

      {/* Download Busy && Error States */}
      {error && (
        <div className="block">
          <p className="sh-body-sm">
            There was an error downloading the attachment
          </p>
        </div>
      )}
      {busy && (
        <p className="sh-body-sm">
          <Spinner className="inline-block mr-1 inherit" size="sm" />{' '}
          Downloading attachment...
        </p>
      )}
    </div>
  )
}
